import { useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronDown, ChevronUp } from "lucide-react";

import ExamScoreTrendChart from "@/components/exam/ExamScoreTrendChart";
import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import { ApiError } from "@/lib/api";
import { getExamHistory, type ExamHistoryEntry } from "@/lib/exam-api";
import { getProfile, type UserProfile } from "@/lib/profile-api";
import { retryWithBackoff } from "@/lib/retry";

/**
 * apps/web :: pages :: ExamHistoryPage
 *
 * Wave 15b (ROADS-355) — the dedicated /exam/history route. Mounted
 * under <ProtectedRoute><OnboardingGate> in App.tsx so the user is
 * guaranteed authed + onboarded by the time we render. Wrapped in
 * LearnerPortalShell (RB-G8) so the left rail is consistent with
 * other authenticated learner pages.
 *
 * Wire / data flow:
 *   1. Fetch the full UserProfile to learn `target_states`.
 *   2. For every target state, call `getExamHistory(stateCode, 50)`.
 *   3. Flatten the per-state attempts into one list, annotate each row
 *      with its source state (the wire shape doesn't include it on the
 *      entry — only on the response envelope), and sort
 *      reverse-chronologically by `submittedAtMs`.
 *
 * The backend route requires a `stateCode` per call (see
 * `backend/src/main/kotlin/.../ExamRoutes.kt::handleHistory`); there is
 * no user-wide variant. Per-state fan-out is the simplest correct shape
 * for MVP — the typical learner has 1–3 target states, so this is at
 * most 3 calls. If/when target_states grows we can introduce a backend
 * batch endpoint and swap the queryFn without touching the page UI.
 *
 * Visual states (mutually exclusive):
 *   - loading: profile + history both in flight; aria-live polite.
 *   - error:   any of the calls failed after the retry helper exhausts;
 *              show a friendly message + retry button (refetch wired).
 *   - empty:   profile + history both succeeded but the merged list is
 *              empty; show CTA back into exam mode for the focus state.
 *   - data:    real <table> with rows in reverse-chrono order.
 *
 * Color-blind safety: the pass/fail badge is text + colour. The visible
 * label is `t("examHistory.badge.pass")` ("PASS") or
 * `t("examHistory.badge.fail")` ("RETRY"). `data-status` exposes the
 * machine-readable variant for tests + CSS, but the visible text is the
 * source of truth — no information is conveyed by colour alone (Hard
 * Rule #7, see CONVENTIONS.md "Re-ratified 2026-04-25" block).
 */

interface AnnotatedAttempt extends ExamHistoryEntry {
  /** Source state from the per-state ExamHistoryResponse envelope. */
  stateCode: string;
}

function formatDate(ms: number, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(ms));
  } catch {
    return new Date(ms).toISOString().slice(0, 10);
  }
}

function formatTimeSpent(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1_000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}m ${s}s`;
}

async function fetchAllHistory(profile: UserProfile): Promise<AnnotatedAttempt[]> {
  const states = profile.target_states.length > 0
    ? profile.target_states
    : profile.current_focus_state
      ? [profile.current_focus_state]
      : [];

  const responses = await Promise.all(
    states.map(async (stateCode) => {
      const res = await retryWithBackoff(() => getExamHistory(stateCode, 50));
      return res.attempts.map<AnnotatedAttempt>((a) => ({
        ...a,
        stateCode: res.stateCode,
      }));
    }),
  );
  // Flatten — sort order is owned by the TanStack table below
  // (default sorting state: Date desc → reverse-chrono, same as before).
  return responses.flat();
}

export default function ExamHistoryPage() {
  const { t } = useTranslation();

  const profileQuery = useQuery<UserProfile | null, Error>({
    queryKey: ["profile"],
    // Mirror getProfile's 404→null contract; surface other errors via
    // the standard error path so the retry button below can re-run.
    queryFn: () => retryWithBackoff(() => getProfile()),
  });

  const historyQuery = useQuery<AnnotatedAttempt[], Error>({
    queryKey: ["exam", "history", "all"],
    enabled: profileQuery.data != null,
    queryFn: () => fetchAllHistory(profileQuery.data as UserProfile),
  });

  // The two queries are sequenced (history depends on profile). Loading
  // means *either* is still in flight (or history is queued waiting on
  // profile). Error means *either* terminally failed.
  const isLoading =
    profileQuery.isLoading ||
    profileQuery.isFetching && profileQuery.data == null ||
    (profileQuery.data != null && historyQuery.isLoading);

  const error = profileQuery.error ?? historyQuery.error ?? null;

  const attempts = historyQuery.data ?? [];

  const focusState = useMemo<string | null>(() => {
    const p = profileQuery.data;
    if (!p) return null;
    return p.current_focus_state ?? p.target_states[0] ?? null;
  }, [profileQuery.data]);

  const handleRetry = (): void => {
    if (profileQuery.error) {
      void profileQuery.refetch();
    }
    if (historyQuery.error || historyQuery.data) {
      void historyQuery.refetch();
    }
  };

  let body: ReactNode;
  if (isLoading) {
    body = (
      <div
        role="status"
        aria-live="polite"
        data-testid="exam-history-loading"
        className="mt-lg rounded-lg border border-brand-primary/10 bg-white p-md text-sm text-neutral-700 shadow-sm"
      >
        {t("examHistory.loading")}
      </div>
    );
  } else if (error) {
    const message =
      error instanceof ApiError
        ? error.message
        : error instanceof Error
          ? error.message
          : String(error);
    body = (
      <div
        role="alert"
        data-testid="exam-history-error"
        className="mt-lg rounded-lg border border-danger/40 bg-danger/5 p-md text-sm text-danger shadow-sm"
      >
        <p>{t("examHistory.error", { message })}</p>
        <button
          type="button"
          data-testid="exam-history-retry"
          onClick={handleRetry}
          className="mt-sm inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("examHistory.retryButton")}
        </button>
      </div>
    );
  } else if (attempts.length === 0) {
    body = (
      <div
        data-testid="exam-history-empty"
        className="mt-lg rounded-lg border border-brand-primary/10 bg-white p-md text-sm text-neutral-800 shadow-sm sm:p-lg"
      >
        <p className="text-base font-semibold text-brand-primary">
          {t("examHistory.empty.heading")}
        </p>
        <p className="mt-xs text-sm text-neutral-700">
          {t("examHistory.empty.body")}
        </p>
        <Link
          to={focusState ? `/exam/${focusState}` : "/dashboard"}
          data-testid="exam-history-empty-cta"
          className="mt-md inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white no-underline transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("examHistory.empty.cta")}
        </Link>
      </div>
    );
  } else {
    body = (
      <>
        <ExamScoreTrendChart
          attempts={attempts}
          passThreshold={
            // Use the most recent attempt's passPercent as the
            // reference line — every attempt in the list shares the
            // same spec value but the freshest read is the safest
            // default if a state's spec changes.
            attempts[0]?.passPercent ?? null
          }
        />
        <ExamHistoryTable attempts={attempts} />
      </>
    );
  }

  return (
    <LearnerPortalShell pageTitle={t("examHistory.title")}>
      <section
        className="mx-auto max-w-4xl px-md pt-xl pb-xl sm:px-lg sm:pt-2xl sm:pb-2xl"
        data-testid="exam-history-page"
      >
        <PageHeader />
        {body}
      </section>
    </LearnerPortalShell>
  );
}

/**
 * The data table inside ExamHistoryPage. Per ADR-0014 the learner-side
 * tables call `useReactTable` headless directly — no shared wrapper.
 * The row is kept as `role="link"` so the existing ROADS-430 AC #4
 * row-as-link affordance survives the column-def refactor.
 */
function ExamHistoryTable({ attempts }: { attempts: AnnotatedAttempt[] }): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const navigateToAttempt = (a: AnnotatedAttempt): void => {
    navigate(`/exam/history/${a.attemptId}`, { state: { attempt: a } });
  };
  const handleRowKeyDown = (
    e: KeyboardEvent<HTMLTableRowElement>,
    a: AnnotatedAttempt,
  ): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigateToAttempt(a);
    }
  };

  const columns = useMemo<ColumnDef<AnnotatedAttempt, unknown>[]>(
    () => [
      {
        id: "date",
        accessorFn: (row) => row.submittedAtMs,
        header: t("examHistory.columns.date"),
        sortDescFirst: false,
        cell: ({ row }) => formatDate(row.original.submittedAtMs, i18n.language),
      },
      {
        id: "state",
        accessorKey: "stateCode",
        header: t("examHistory.columns.state"),
        cell: ({ row }) => (
          <span className="font-semibold">{row.original.stateCode}</span>
        ),
      },
      {
        id: "score",
        // Ratio (not absolute correctCount) so a 9/10 sorts above 8/12.
        accessorFn: (row) => row.correctCount / row.questionCount,
        header: t("examHistory.columns.score"),
        sortDescFirst: false,
        cell: ({ row }) =>
          t("examHistory.score", {
            correct: row.original.correctCount,
            total: row.original.questionCount,
          }),
      },
      {
        id: "result",
        header: t("examHistory.columns.result"),
        enableSorting: false,
        cell: ({ row }) => {
          const a = row.original;
          const status = a.passed ? "pass" : "fail";
          const badgeLabel = a.passed
            ? t("examHistory.badge.pass")
            : t("examHistory.badge.fail");
          const badgeClass = a.passed
            ? "border-success/40 bg-success/10 text-success"
            : "border-amber-500/40 bg-amber-500/10 text-amber-700";
          return (
            <span
              data-testid="exam-history-badge"
              data-status={status}
              className={`inline-flex items-center rounded-full border px-sm py-xs text-xs font-semibold uppercase tracking-wide ${badgeClass}`}
            >
              {badgeLabel}
            </span>
          );
        },
      },
      {
        id: "time",
        accessorFn: (row) => row.timeSpentMs,
        header: t("examHistory.columns.time"),
        sortDescFirst: false,
        cell: ({ row }) => formatTimeSpent(row.original.timeSpentMs),
      },
    ],
    [t, i18n.language],
  );

  const [sorting, setSorting] = useState<SortingState>([
    // Reverse-chrono on first paint (newest first) — the same default
    // the old in-memory `.sort()` produced, now expressed as table state.
    { id: "date", desc: true },
  ]);

  const table = useReactTable({
    data: attempts,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.attemptId,
  });

  return (
    <div className="mt-lg overflow-x-auto rounded-lg border border-brand-primary/10 bg-white shadow-sm">
      <table
        data-testid="exam-history-table"
        className="w-full border-collapse text-left text-sm"
      >
        <caption className="sr-only">{t("examHistory.tableCaption")}</caption>
        <thead className="bg-brand-surface/40 text-xs font-semibold uppercase tracking-wide text-neutral-700">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => {
                const sortable =
                  !header.isPlaceholder && header.column.getCanSort();
                const sort = header.column.getIsSorted();
                const headerLabel =
                  typeof header.column.columnDef.header === "string"
                    ? header.column.columnDef.header
                    : header.id;
                return (
                  <th
                    key={header.id}
                    scope="col"
                    className="px-sm py-sm sm:px-md"
                    aria-sort={
                      sort === "asc"
                        ? "ascending"
                        : sort === "desc"
                          ? "descending"
                          : "none"
                    }
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        aria-label={t("examHistory.sortBy", {
                          column: headerLabel,
                          defaultValue: `Sort by ${headerLabel}`,
                        })}
                        className="inline-flex items-center gap-xs uppercase tracking-wide text-neutral-700 hover:text-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {sort === "asc" && (
                          <ChevronUp aria-hidden="true" className="h-3 w-3" />
                        )}
                        {sort === "desc" && (
                          <ChevronDown aria-hidden="true" className="h-3 w-3" />
                        )}
                      </button>
                    ) : header.isPlaceholder ? null : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const a = row.original;
            const badgeLabel = a.passed
              ? t("examHistory.badge.pass")
              : t("examHistory.badge.fail");
            const rowAriaLabel = t("examHistory.rowAriaLabel", {
              attemptNumber: a.attemptNumber,
              date: formatDate(a.submittedAtMs, i18n.language),
              result: badgeLabel,
              correct: a.correctCount,
              total: a.questionCount,
            });
            return (
              <tr
                key={row.id}
                data-testid={`exam-history-row-${a.attemptId}`}
                data-attempt-id={a.attemptId}
                // ROADS-430 AC #4 — preserved through the TanStack port.
                role="link"
                aria-label={rowAriaLabel}
                tabIndex={0}
                onClick={() => navigateToAttempt(a)}
                onKeyDown={(e) => handleRowKeyDown(e, a)}
                className="cursor-pointer border-t border-brand-primary/10 outline-none hover:bg-brand-primary/5 focus-visible:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-sm py-sm sm:px-md">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PageHeader(): JSX.Element {
  const { t } = useTranslation();
  return (
    <header className="flex flex-col gap-xs">
      <p className="text-sm font-semibold uppercase tracking-wide text-neutral-600">
        {t("examHistory.eyebrow")}
      </p>
      <h1 className="font-display text-3xl font-bold tracking-tight text-brand-primary sm:text-4xl">
        {t("examHistory.title")}
      </h1>
      <p className="text-sm text-neutral-700">{t("examHistory.subtitle")}</p>
    </header>
  );
}
